import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { shopApi } from '@wa/shared';
import { Card, Field, Notice, Switch, errorText, input, primary, secondary } from './ui';

/**
 * The first screen: is the shop on, what is its address, and the link to share.
 * Creating the shop is saving a name and an address; everything else has a
 * default the client can change later.
 */
export function OverviewPanel() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['shop', 'settings'], queryFn: shopApi.settings });
  const shop = settings.data?.settings ?? null;

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (shop) { setName(shop.name); setSlug(shop.slug); setSlugTouched(true); }
  }, [shop]);

  const check = useQuery({
    queryKey: ['shop', 'slug-check', slug || null, slugTouched ? null : name],
    queryFn: () => shopApi.slugCheck(slug || null, slugTouched ? null : name),
    enabled: !shop || slug !== shop.slug,
  });

  useEffect(() => {
    if (!slugTouched && check.data?.slug) setSlug(check.data.slug);
  }, [check.data, slugTouched]);

  const save = useMutation({
    mutationFn: (patch: Parameters<typeof shopApi.saveSettings>[0]) => shopApi.saveSettings(patch),
    onSuccess: () => { setError(null); void queryClient.invalidateQueries({ queryKey: ['shop'] }); },
    onError: (e) => setError(errorText(e, 'Could not save the shop.')),
  });

  const link = useQuery({ queryKey: ['shop', 'link'], queryFn: () => shopApi.link(), enabled: !!shop?.isEnabled });

  if (settings.isLoading) return <Notice kind="muted">Loading…</Notice>;

  return (
    <div className="flex flex-col gap-4">
      {error && <Notice kind="bad">{error}</Notice>}

      <Card title={shop ? 'Your shop' : 'Create your shop'}
            blurb={shop ? 'The name customers see at the top of the page, and the address the link opens.' : 'A name and an address are enough to start. Products, prices and the page’s look come next.'}>
        <div className="grid gap-3 @min-[32rem]:grid-cols-2">
          <Field label="Shop name">
            <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Just For You Gifts" maxLength={120} />
          </Field>
          <Field label="Address" hint={settings.data ? `${settings.data.shopUrl}/${slug || '…'}` : undefined}>
            <input className={input} value={slug} onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase()); }} placeholder="just-for-you-gifts" maxLength={40} />
          </Field>
        </div>
        {check.data && (!check.data.valid || !check.data.available) && (
          <Notice kind="bad">
            {!check.data.valid ? 'Lower-case letters, digits and single hyphens, 3–40 characters, not a reserved word.' : `That address is taken. Try ${check.data.suggestion}.`}
          </Notice>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={primary} disabled={save.isPending || !name.trim() || !slug}
                  onClick={() => save.mutate({ name: name.trim(), slug })}>
            {save.isPending ? 'Saving…' : shop ? 'Save' : 'Create shop'}
          </button>
          {shop && (
            <Switch checked={shop.isEnabled} onChange={(v) => save.mutate({ isEnabled: v })} label="Shop is live"
                    hint={shop.isEnabled ? 'Customers can open the link.' : 'The link answers "no shop here" until this is on.'} />
          )}
        </div>
      </Card>

      {shop?.isEnabled && (
        <Card title="Share the link" blurb="Paste it into a chat, a broadcast template’s button, or print the address. A link copied from a customer’s page carries their identity and opens priced for them.">
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded border border-(--color-line) bg-(--color-sunken) px-2 py-1.5 text-small">{link.data?.url ?? '…'}</code>
            <button type="button" className={secondary} disabled={!link.data}
                    onClick={async () => { if (link.data) { await navigator.clipboard.writeText(link.data.url); setCopied(true); setTimeout(() => setCopied(false), 1500); } }}>
              {copied ? 'Copied' : 'Copy'}
            </button>
            {link.data && <a className={secondary} href={link.data.url} target="_blank" rel="noreferrer">Open</a>}
          </div>
          <p className="text-micro text-(--color-muted)">
            Catalogue version {shop.catalogueVersion} · {shop.orderingPaused ? 'ordering paused' : 'taking orders'} · {shop.hidePricesUntilSignin ? 'prices after sign-in' : 'prices shown to everyone'}
          </p>
        </Card>
      )}
    </div>
  );
}
